import moment from "moment";
import {
  Box, Flex, FormControl, FormLabel, Switch,
} from "@chakra-ui/react";
import AttributeButton from "components/Button/AttributeButton";
import WeekCalendar from "components/Calendar/WeekCalendar";
import { CalendarEvent } from "components/Calendar/types";

interface ScheduleWeekViewProps {
  events: CalendarEvent[];
  selectedDate: moment.Moment;
  timezone: string;
  showWeekends: boolean;
  onDateChange: (newDate: moment.Moment) => void;
  onShowWeekendsChange: (value: boolean) => void;
  onEventClick: (event: CalendarEvent) => void;
  onEventUpdate: (id: string, newStart: Date, newEnd: Date) => void;
}

export default function ScheduleWeekView({
  events,
  selectedDate,
  timezone,
  showWeekends,
  onDateChange,
  onShowWeekendsChange,
  onEventClick,
  onEventUpdate,
}: ScheduleWeekViewProps) {
  // Calculate week start date (ISO week starts on Monday)
  const weekStart = selectedDate.clone().startOf("isoWeek");

  // Calculate date label
  const weekEnd = showWeekends
    ? weekStart.clone().add(6, "days")
    : weekStart.clone().add(4, "days");
  const dateLabel = `${weekStart.format("MMM D")} - ${weekEnd.format("MMM D, YYYY")}`;

  // Navigation handlers
  const handlePrevious = () => {
    onDateChange(selectedDate.clone().subtract(1, "week"));
  };

  const handleNext = () => {
    onDateChange(selectedDate.clone().add(1, "week"));
  };

  return (
    <Flex direction="column" width="100%">
      {/* Calendar controls */}
      <Flex justify="space-between" align="center" px="4" py="2">
        <FormControl display="flex" alignItems="center" width="auto">
          <FormLabel htmlFor="weekends-toggle-week" mb="0">Show weekends</FormLabel>
          <Switch
            id="weekends-toggle-week"
            isChecked={showWeekends}
            onChange={(e) => onShowWeekendsChange(e.target.checked)}
          />
        </FormControl>
        <Box fontWeight="bold" fontSize="lg">{dateLabel}</Box>
        <Flex>
          <AttributeButton onClick={handlePrevious}>← Prev</AttributeButton>
          <AttributeButton onClick={handleNext}>Next →</AttributeButton>
        </Flex>
      </Flex>

      {/* Week calendar */}
      <WeekCalendar
        events={events}
        onUpdate={onEventUpdate}
        onCreate={() => {}}
        onEventClick={onEventClick}
        startDate={weekStart.toDate()}
        timezone={timezone}
        showWeekends={showWeekends}
        editable
      />
    </Flex>
  );
}
